import { useContext, useEffect } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { isGoogleDisconnectPending } from "src/utils/googleConnect.js";

export const GOOGLE_DISCONNECT_REFRESH_MS = 15000;
export const GOOGLE_DISCONNECT_SLOW_REFRESH_MS = 60000;
export const GOOGLE_DISCONNECT_FAST_REFRESH_WINDOW_MS = 2 * 60 * 1000;

/**
 * Reads the user again while their Google Connect disconnect is pending, so a surface
 * showing it picks up the moment the removal finishes. Polls often for the length of a
 * normal run, then backs off, and stops while the tab is hidden.
 * @param {boolean} [enabled=true] - Whether the surface is showing.
 */
export function useGoogleDisconnectRefresh(enabled = true) {
  const { user, refetchAuth } = useContext(AuthContext);
  const pending = enabled && isGoogleDisconnectPending(user);

  useEffect(() => {
    if (!pending) return undefined;
    let startedAt = Date.now();
    let hiddenAt = null;
    let timer = null;
    const refresh = () => refetchAuth?.({ silent: true });
    const schedule = () => {
      const delay =
        Date.now() - startedAt < GOOGLE_DISCONNECT_FAST_REFRESH_WINDOW_MS
          ? GOOGLE_DISCONNECT_REFRESH_MS
          : GOOGLE_DISCONNECT_SLOW_REFRESH_MS;
      timer = setTimeout(() => {
        timer = null;
        if (document.visibilityState === "hidden") return;
        refresh();
        schedule();
      }, delay);
    };
    const stop = () => {
      clearTimeout(timer);
      timer = null;
      hiddenAt ??= Date.now();
    };
    // Reads once and polls again; a long hidden stretch earns a fresh fast window, a quick tab switch does not
    const resume = () => {
      if (timer || document.visibilityState === "hidden") return;
      if (hiddenAt !== null && Date.now() - hiddenAt >= GOOGLE_DISCONNECT_FAST_REFRESH_WINDOW_MS) {
        startedAt = Date.now();
      }
      hiddenAt = null;
      refresh();
      schedule();
    };
    const onVisibilityChange = () => (document.visibilityState === "hidden" ? stop() : resume());

    resume();
    document.addEventListener("visibilitychange", onVisibilityChange);
    // pagehide/pageshow cover a bfcache round trip, which does not reliably fire visibilitychange
    window.addEventListener("pagehide", stop);
    window.addEventListener("pageshow", resume);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", stop);
      window.removeEventListener("pageshow", resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);
}
